import Handlebars from 'handlebars';
import type React from 'react';
import { useMemo } from 'react';

// Pretty-print JSON
const beautifyJSON = (json: string) => {
  try {
    return JSON.stringify(JSON.parse(json), null, 2);
  } catch {
    return json; // fallback to raw if invalid JSON
  }
};

// Preprocess template: remove `{% raw %}` wrappers
const cleanTemplate = (template: string) => template.replace(/{% raw %}([\s\S]*?){% endraw %}/g, '$1');

interface TemplatePreviewProps {
  template: string;
  templateType: 'Html' | 'PlainText' | 'Json' | 'Generic';
  data?: Record<string, any>;
}

const TemplatePreview: React.FC<TemplatePreviewProps> = ({ template, templateType, data = {} }) => {
  const mockData = {
    // siteUrl: 'https://example.com',
    // siteLogoUrl: 'https://via.placeholder.com/300x80?text=Logo',
    // siteTitle: 'Example Site',
    // resetPasswordUrl: 'https://example.com/reset-password',
    // userName: 'John Doe',
    ...data,
  };

  const renderedContent = useMemo(() => {
    try {
      const cleaned = cleanTemplate(template);

      if (templateType === 'Html') {
        // compile and render handlebars
        const compiled = Handlebars.compile(cleaned);
        return compiled(mockData);
      }

      if (templateType === 'PlainText' || templateType === 'Json') {
        // Replace placeholders manually for plain text
        const replaced = cleaned.replace(/{{(.*?)}}/g, (_, key) => mockData[key.trim()] ?? `{{${key}}}`);
        return replaced;
      }

      // if (templateType === 'Json1') {
      //   // Safely replace placeholders (keep JSON valid)
      //   const replaced = cleaned.replace(/{{(.*?)}}/g, (_, key) => {
      //     const value = mockData[key.trim()];
      //     // Always return a valid JSON string literal, even if undefined
      //     return value !== undefined ? String(value) : '';
      //   });

      //   // If it's wrapped in quotes, don't double-quote it
      //   const safeJson = replaced.replace(/"\s*"\s*/g, '""'); // optional cleanup

      //   return beautifyJSON(safeJson);
      // }

      return template;
    } catch (err) {
      console.error('Preview render error:', err);
      return 'Error rendering template preview';
    }
  }, [template, templateType, data]);

  // Render based on engine type
  if (templateType === 'Html') {
    return (
      <iframe
        title="HTML Template Preview"
        srcDoc={renderedContent}
        style={{
          width: '100%',
          height: '37vh',
          border: '1px solid #ccc',
          borderRadius: 8,
          background: '#fff',
        }}
      />
    );
  }
  if (templateType === 'Generic') {
    return (
      <pre
        style={{
          fontFamily: 'monospace',
          whiteSpace: 'pre-wrap',
          background: '#f9f9f9',
          padding: '1rem',
          borderRadius: 8,
          height: '37vh',
          overflow: 'auto',
          width: '100%',
        }}
      >
        {template}
      </pre>
    );
  }

  if (templateType === 'PlainText' || templateType === 'Json') {
    return (
      <pre
        style={{
          whiteSpace: 'pre-wrap',
          fontFamily: 'monospace',
          padding: '1rem',
          background: '#f7f7f7',
          border: '1px solid #ccc',
          borderRadius: 8,
          height: '37vh',
          width: '100%',
          overflow: 'auto',
        }}
      >
        {renderedContent}
      </pre>
    );
  }

  // if (templateType === 'Json1') {
  //   return (
  //     <SyntaxHighlighter
  //       language="json"
  //       style={oneDark}
  //       customStyle={{
  //         width: '100%',
  //         height: '37vh',
  //         overflow: 'auto',
  //         borderRadius: 8,
  //         padding: '1rem',
  //         boxSizing: 'border-box',
  //       }}
  //     >
  //       {renderedContent}
  //     </SyntaxHighlighter>
  //   );
  // }

  return null;
};

export default TemplatePreview;
